/**
 * Einheitliche Lieferantenzahlungen — ohne Doppelzählung.
 * Gleiche Logik wie EnhancedCashReportModal / cashFlowBalance.
 */
import { isExpensePaymentMirror } from './cashFlowBalance';

function buildFallbackPaymentId(prefix, entry) {
  return `${prefix}_${entry?.id || entry?.invoiceId || entry?.referenceId || entry?.paymentDate || entry?.date || entry?.createdAt || 'no-date'}_${entry?.amount || entry?.totalAmount || 0}`;
}

function paymentDedupeKey(payment) {
  if (payment?.id) return `payment_${payment.id}`;
  return `payment_${buildFallbackPaymentId('fb', payment)}`;
}

/**
 * Sammelt alle Lieferantenzahlungen (supermarketPayments + invoicePayments + invoice.payments)
 * und entfernt Duplikate (normale + konsolidierte Rechnungen).
 */
export function collectUniqueSupplierPayments({
  supermarketPayments = [],
  invoicePayments = [],
  supermarketInvoices = []
} = {}) {
  const seen = new Set();
  const result = [];

  const pushUnique = (payment) => {
    if (!payment || isExpensePaymentMirror(payment)) return;
    if (payment.consolidatedMirror) return;
    const key = paymentDedupeKey(payment);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(payment);
  };

  [...(Array.isArray(supermarketPayments) ? supermarketPayments : []),
    ...(Array.isArray(invoicePayments) ? invoicePayments : [])].forEach(pushUnique);

  (Array.isArray(supermarketInvoices) ? supermarketInvoices : []).forEach((invoice) => {
    if (!invoice || !Array.isArray(invoice.payments)) return;
    invoice.payments.forEach((paymentEntry) => {
      if (!paymentEntry) return;
      pushUnique({
        ...paymentEntry,
        invoiceId: paymentEntry.invoiceId || invoice.id,
        supplierId: paymentEntry.supplierId || invoice.supplierId,
        supplierName: paymentEntry.supplierName || invoice.supplierName,
        paymentDate:
          paymentEntry.paymentDate ||
          paymentEntry.createdAt ||
          invoice.lastPaymentDate ||
          invoice.updatedAt
      });
    });
  });

  return result;
}

export function paymentFlowDate(entry) {
  const raw = entry?.paymentDate || entry?.date || entry?.createdAt || entry?.timestamp;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function paymentFlowAmount(entry) {
  const n = Number(entry?.amount ?? entry?.totalAmount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function sumSupplierPaymentAmounts(entries) {
  return (Array.isArray(entries) ? entries : []).reduce(
    (sum, entry) => sum + paymentFlowAmount(entry),
    0
  );
}

export function filterSupplierPaymentsByPeriod(entries, {
  days = 7,
  now = new Date(),
  start = null,
  end = null,
} = {}) {
  if (start && end) {
    const rangeStart = start instanceof Date ? start : new Date(start);
    const rangeEnd = end instanceof Date ? end : new Date(end);
    return (Array.isArray(entries) ? entries : []).filter((entry) => {
      const d = paymentFlowDate(entry);
      if (!d) return false;
      return d >= rangeStart && d <= rangeEnd;
    });
  }

  const ref = now instanceof Date ? now : new Date(now);
  let cutoffDate = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  cutoffDate.setHours(0, 0, 0, 0);

  if (days !== 1) {
    cutoffDate = new Date(cutoffDate);
    cutoffDate.setDate(cutoffDate.getDate() - days);
  }

  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const d = paymentFlowDate(entry);
    if (!d) return false;
    if (days === 1) {
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const todayDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      return day.getTime() === todayDay.getTime();
    }
    return d >= cutoffDate;
  });
}
